/* WSH · Find Your Sound · scorer (Phase A2 · pure function)
 *
 *   fysScore({ answers, catalog, config, owned = [], rounds = [] })
 *     → { anchor, match, next, wildcard, guitar_layer, strip, reasons }
 *
 * Contract:
 *   - Pure and deterministic: same input → same output every call.
 *   - No DOM. No fetch. No Shopify. No side effects.
 *   - Node-runnable (CommonJS) AND browser-loadable (<script> tag).
 *   - Weights + lists in `config`, not hardcoded here.
 *   - `owned` is empty in FYS-2; FYS-5 fills it.
 *   - `rounds` is empty in FYS-2 (hook for later rounds 2–4 nudging anchor).
 *
 * Phase A2 changes (Phil, 2026-09-30):
 *   1. Genre membership is tile+collection additive per genre×format, sourced
 *      from config.full_band_genres and config.drum_genres. Both use
 *      full_band_genres (Phil amendment #1). No more p.genre for pool decisions.
 *   2. wav_only handles (config.wav_only_handles) can never be the match. They
 *      still qualify as next or wildcard.
 *   3. Scorer returns up to 3 options per slot on next + wildcard.
 *      - next.options[]: top 3 of shortlist ranked by preferences.
 *      - wildcard.options[]: best per neighbour genre, in neighbour order,
 *        filled from other genres if <3.
 *      - No duplicates across match / next.options / wildcard.options.
 *   4. Guitar layer (full/both only): pickGuitarLayer returns nearest
 *      guitar_parts to anchor (same-genre first, then neighbours). null if
 *      no guitar_parts candidate.
 */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.fysScore = factory().fysScore;
    root.__fysScoreInternal = factory();
  }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var CANONICAL_GENRES = ['Country', 'Rock', 'Jazz & Blues', 'Indie', 'Cinematic', 'Pop'];

  // ══════════════════════════════════════════════════════════════════════════
  // Anchor derivation (§5.3)
  // ══════════════════════════════════════════════════════════════════════════

  var FALLBACK_ANCHOR_MAP = {
    energy: { 'laid-back': 28, 'steady': 50, 'driving': 76 },
    era:    { 'vintage': 20, 'between': 50, 'modern': 80 },
    production: { 'vintage': 20, 'between': 50, 'modern': 80 },
  };

  function anchorFromLib(lib) {
    return {
      energy:     Number(lib.mood_energy),
      tone:       Number(lib.mood_tone),
      era:        Number(lib.mood_era),
      production: Number(lib.mood_production),
    };
  }

  function anchorFromFallback(fb) {
    var eKey = fb.energy || 'steady';
    var fKey = fb.feel || 'between';
    var e = FALLBACK_ANCHOR_MAP.energy[eKey];
    var er = FALLBACK_ANCHOR_MAP.era[fKey];
    var pr = FALLBACK_ANCHOR_MAP.production[fKey];
    if (e == null) e = 50;
    if (er == null) er = 50;
    if (pr == null) pr = 50;
    return { energy: e, tone: 40, era: er, production: pr };
  }

  function medianAnchor(pool) {
    if (!pool.length) return { energy: 50, tone: 50, era: 50, production: 50 };
    var keys = ['energy', 'tone', 'era', 'production'];
    var out = {};
    keys.forEach(function (k) {
      var vals = [];
      for (var i = 0; i < pool.length; i++) {
        var v = Number(pool[i]['mood_' + k]);
        if (isFinite(v)) vals.push(v);
      }
      vals.sort(function (a, b) { return a - b; });
      out[k] = vals.length ? vals[Math.floor(vals.length / 2)] : 50;
    });
    return out;
  }

  function computeAnchor(answers, byHandle, formatPool) {
    if (answers.pick && answers.pick.library_handle) {
      var picked = byHandle[answers.pick.library_handle];
      if (picked) return { values: anchorFromLib(picked), source: 'pick' };
    }
    if (answers.fallback) {
      return { values: anchorFromFallback(answers.fallback), source: 'fallback' };
    }
    if (answers.from) {
      var fromLib = byHandle[answers.from];
      if (fromLib) return { values: anchorFromLib(fromLib), source: 'from' };
    }
    return { values: medianAnchor(formatPool), source: 'genre-median' };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Distance (§5.4)
  // ══════════════════════════════════════════════════════════════════════════

  function distance(anchor, lib, weights) {
    if (!weights) weights = { energy: 0.35, era: 0.25, production: 0.25, tone: 0.15 };
    var keys = ['energy', 'tone', 'era', 'production'];
    var missing = false;
    var sum = 0;
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var libV = Number(lib['mood_' + k]);
      if (!isFinite(libV)) { missing = true; continue; }
      var d = (anchor[k] - libV);
      sum += weights[k] * d * d;
    }
    if (missing) return { dist: Infinity, missing: true };
    return { dist: Math.sqrt(sum), missing: false };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Genre-membership lookup (Phase A2)
  // ══════════════════════════════════════════════════════════════════════════

  function libsInGenreForFormat(format, genre, config) {
    // Returns the set of library handles considered part of (genre, format).
    // 'full' and 'both' share config.full_band_genres. 'drums' uses config.drum_genres.
    if (format === 'drums') {
      return new Set(((config.drum_genres || {})[genre]) || []);
    }
    return new Set(((config.full_band_genres || {})[genre]) || []);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Pool selection (§5.2 + Phil A2 changes)
  // ══════════════════════════════════════════════════════════════════════════

  function poolFor(format, genre, catalog, config, owned) {
    var out = [];
    var i, p;
    var neverInclude = new Set(owned || []);
    var wildcardOnly = new Set(config.wildcard_only_handles || []);
    var members = libsInGenreForFormat(format, genre, config);

    if (format === 'drums') {
      for (i = 0; i < catalog.length; i++) {
        p = catalog[i];
        if (p.type !== 'Drum Product') continue;
        if (!members.has(p.handle)) continue;
        if (neverInclude.has(p.handle)) continue;
        if (wildcardOnly.has(p.handle)) continue;
        out.push(p);
      }
      return out;
    }
    // full / both → Singles, genre-matched (tile+collection), minus guitar_parts
    var guitarParts = new Set(config.guitar_parts_handles || []);
    for (i = 0; i < catalog.length; i++) {
      p = catalog[i];
      if (p.type !== 'Single Product') continue;
      if (!members.has(p.handle)) continue;
      if (neverInclude.has(p.handle)) continue;
      if (guitarParts.has(p.handle)) continue;
      if (wildcardOnly.has(p.handle)) continue;
      out.push(p);
    }
    return out;
  }

  function pairedEditionOf(lib, byHandle) {
    if (!lib || !lib.paired_edition) return null;
    return byHandle[lib.paired_edition] || null;
  }

  function inSameCuratedBundle(a, b) {
    if (!a || !b) return null;
    var ba = a.curated_bundle_memberships || [];
    var bb = b.curated_bundle_memberships || [];
    for (var i = 0; i < ba.length; i++) {
      if (bb.indexOf(ba[i]) >= 0) return ba[i];
    }
    return null;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Sales tie-break + not-first enforcement (Phil A3)
  // ══════════════════════════════════════════════════════════════════════════

  var SALES_TIE_BREAK_WINDOW = 5;   // distance points from top-ranked; within
                                    // this band, reorder by sales_12m desc.

  function _sales(config, handle) {
    var s = (config.sales_12m || {})[handle];
    return typeof s === 'number' ? s : 0;
  }

  function _isNotFirst(config, handle) {
    return (config.not_first || []).indexOf(handle) >= 0;
  }

  function applySalesTieBreak(rankedList, config) {
    // rankedList is already ordered by (preference, distance). Take candidates
    // whose distance is within SALES_TIE_BREAK_WINDOW of the top-ranked's dist;
    // sort THAT slice by sales_12m desc (with distance as tiebreaker, then handle).
    if (rankedList.length <= 1) return rankedList;
    var topDist = rankedList[0].dist;
    if (!isFinite(topDist)) return rankedList;
    var band = [];
    var tail = [];
    for (var i = 0; i < rankedList.length; i++) {
      if (isFinite(rankedList[i].dist) && (rankedList[i].dist - topDist) <= SALES_TIE_BREAK_WINDOW) {
        band.push(rankedList[i]);
      } else {
        tail.push(rankedList[i]);
      }
    }
    band.sort(function (a, b) {
      var sa = _sales(config, a.lib.handle), sb = _sales(config, b.lib.handle);
      if (sa !== sb) return sb - sa;
      if (a.dist !== b.dist) return a.dist - b.dist;
      return a.lib.handle < b.lib.handle ? -1 : 1;
    });
    return band.concat(tail);
  }

  function enforceNotFirst(orderedList, config) {
    // Ensure orderedList[0]'s handle is not in config.not_first. If it is, swap
    // in the first non-not_first entry. If ALL are not_first, keep as-is.
    if (!orderedList.length) return orderedList;
    if (!_isNotFirst(config, orderedList[0].lib.handle)) return orderedList;
    for (var i = 1; i < orderedList.length; i++) {
      if (!_isNotFirst(config, orderedList[i].lib.handle)) {
        var out = orderedList.slice();
        var swapped = out.splice(i, 1)[0];
        out.unshift(swapped);
        return out;
      }
    }
    return orderedList; // fallback: all are not_first
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Match slot (§5.5) — not_first enforced (wav_only migrated onto this rule)
  // ══════════════════════════════════════════════════════════════════════════

  function pickMatch(answers, pool, anchor, byHandle, weights, config) {
    // not_first libraries can never be a "primary" pick (match / option 1
    // anywhere). Wav_only entries live inside not_first now.
    var eligible = pool.filter(function (p) { return !_isNotFirst(config, p.handle); });

    // Picked by ear
    if (answers.pick && answers.pick.library_handle) {
      var pickedHandle = answers.pick.library_handle;
      var poolIx = eligible.findIndex(function (p) { return p.handle === pickedHandle; });
      if (poolIx >= 0) {
        var picked = eligible[poolIx];
        var d = distance(anchor.values, picked, weights);
        return { lib: picked, dist: d.dist, missing_mood: d.missing, source: 'pick' };
      }
      // Removed from eligible (owned / inactive / never_recommend / wav_only).
      // Use its anchor (already set in computeAnchor), pick nearest in-genre.
    }
    // Otherwise: nearest to anchor within eligible pool.
    // Phil A3 amend (2026-09-30): apply sales tie-break to the match slot too
    // — candidates within SALES_TIE_BREAK_WINDOW of the nearest, sorted by
    // sales_12m desc, then dist, then handle. not_first already filtered out
    // at `eligible` computation above. Picked-by-ear match (the branch above)
    // is unchanged: it stays exactly the pick.
    if (!eligible.length) return null;
    var ranked = eligible.map(function (p) {
      var d = distance(anchor.values, p, weights);
      return { lib: p, dist: d.dist, missing_mood: d.missing };
    }).sort(function (a, b) {
      if (a.dist !== b.dist) return a.dist - b.dist;
      return a.lib.handle < b.lib.handle ? -1 : 1;
    });
    var reordered = applySalesTieBreak(ranked, config);
    var top = reordered[0];
    return { lib: top.lib, dist: top.dist, missing_mood: top.missing_mood, source: 'nearest' };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Next-step OPTIONS (§5.6 + Phil A2 #5)
  // ══════════════════════════════════════════════════════════════════════════

  function rankByPreference(cands, match, chosenDaw) {
    // Returns cands annotated with `preference` and sorted by (preferenceRank, dist).
    var matchFirstSub = (match.sub_genres && match.sub_genres[0]) || null;
    function rankOf(cand) {
      if (inSameCuratedBundle(match, cand.lib)) return { r: 0, pref: 'shared-bundle' };
      var subs = cand.lib.sub_genres || [];
      if (subs[0] && subs[0] !== matchFirstSub) return { r: 1, pref: 'different-sub-genre' };
      var keys = cand.lib.daw_session_keys || [];
      if (chosenDaw && keys.indexOf(chosenDaw) >= 0) return { r: 2, pref: 'daw-match' };
      return { r: 3, pref: 'nearest' };
    }
    var scored = cands.map(function (c) {
      var r = rankOf(c);
      return Object.assign({}, c, { preferenceRank: r.r, preference: r.pref });
    });
    scored.sort(function (a, b) {
      if (a.preferenceRank !== b.preferenceRank) return a.preferenceRank - b.preferenceRank;
      if (a.dist !== b.dist) return a.dist - b.dist;
      return a.lib.handle < b.lib.handle ? -1 : 1;
    });
    return scored;
  }

  function pickNextStepOptions(answers, pool, anchor, match, byHandle, weights, catalog, config) {
    var shortlist = config.next_step_shortlist || 4;
    var pickedHandle = (answers.pick && answers.pick.library_handle) || null;
    var pairedMatch = pairedEditionOf(match, byHandle);

    var base = pool.filter(function (p) {
      if (p.handle === match.handle) return false;
      if (pairedMatch && p.handle === pairedMatch.handle) return false;
      if (pickedHandle && p.handle === pickedHandle && p.handle !== match.handle) return false;
      return true;
    });

    var fromNeighbours = false;
    // THIN-POOL SAFETY NET
    if (base.length === 0) {
      fromNeighbours = true;
      var neighbours = (config.neighbours || {})[answers.genre] || [];
      var extra = [];
      neighbours.forEach(function (ng) {
        var np = poolFor(answers.format, ng, catalog, config, []).filter(function (p) {
          if (p.handle === match.handle) return false;
          if (pairedMatch && p.handle === pairedMatch.handle) return false;
          return true;
        });
        extra = extra.concat(np);
      });
      base = extra;
      if (base.length === 0) return { options: [], from_neighbours: false };
    }

    var scored = base.map(function (p) {
      var d = distance(anchor.values, p, weights);
      return { lib: p, dist: d.dist, missing_mood: d.missing };
    }).sort(function (a, b) {
      if (a.dist !== b.dist) return a.dist - b.dist;
      return a.lib.handle < b.lib.handle ? -1 : 1;
    });

    var top = scored.slice(0, Math.min(shortlist, scored.length));
    var chosenDaw = answers.daw || null;
    var ranked = rankByPreference(top, match, chosenDaw);
    // Sales tie-break within SALES_TIE_BREAK_WINDOW of top-ranked dist (Phil A3 #1).
    var reordered = applySalesTieBreak(ranked, config);
    // Never let a not_first library sit at option 1 (Phil A3 #2).
    var final = enforceNotFirst(reordered, config);
    var options = final.slice(0, 3).map(function (r) {
      return {
        handle: r.lib.handle,
        title: r.lib.title,
        genre: r.lib.genre,
        dist: r.dist,
        missing_mood: !!r.missing_mood,
        preference: r.preference,
        from_neighbours: fromNeighbours,
        sales_12m: _sales(config, r.lib.handle),
      };
    });
    return { options: options, from_neighbours: fromNeighbours };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Wildcard OPTIONS (§5.7 + Phil A2 #5)
  // ══════════════════════════════════════════════════════════════════════════

  function libsInGenreExcluding(format, genre, catalog, config, excludeHandles, owned) {
    var members = libsInGenreForFormat(format, genre, config);
    var neverInclude = new Set(owned || []);
    var out = [];
    for (var i = 0; i < catalog.length; i++) {
      var p = catalog[i];
      var typeOK = (format === 'drums') ? p.type === 'Drum Product' : p.type === 'Single Product';
      if (!typeOK) continue;
      if (!members.has(p.handle)) continue;
      if (neverInclude.has(p.handle)) continue;
      if (excludeHandles.has(p.handle)) continue;
      // Wildcard slot: wildcard_only ALLOWED. guitar_parts NOT allowed for full/both.
      if (format !== 'drums' && (config.guitar_parts_handles || []).indexOf(p.handle) >= 0) continue;
      out.push(p);
    }
    return out;
  }

  function pickWildcardOptions(answers, catalog, config, owned, anchor, match, nextOptions, byHandle, weights) {
    var pairedMatch = pairedEditionOf(match, byHandle);
    var excludeHandles = new Set();
    excludeHandles.add(match.handle);
    if (pairedMatch) excludeHandles.add(pairedMatch.handle);
    var pickedHandle = (answers.pick && answers.pick.library_handle) || null;
    if (pickedHandle) excludeHandles.add(pickedHandle);
    (nextOptions || []).forEach(function (n) {
      excludeHandles.add(n.handle);
      var np = byHandle[n.handle];
      if (np && np.paired_edition) excludeHandles.add(np.paired_edition);
    });

    var sourceGenre = answers.genre;
    var neighbours = ((config.neighbours || {})[sourceGenre]) || [];

    function bestFromGenre(g) {
      var cands = libsInGenreExcluding(answers.format, g, catalog, config, excludeHandles, owned);
      // Exclude libraries that ALSO appear in source-genre's list (dual-membership rule).
      var sourceMembers = libsInGenreForFormat(answers.format, sourceGenre, config);
      cands = cands.filter(function (p) { return !sourceMembers.has(p.handle); });
      if (!cands.length) return null;
      var scored = cands.map(function (p) {
        var d = distance(anchor.values, p, weights);
        return { lib: p, dist: d.dist, missing_mood: d.missing, genre: g };
      }).sort(function (a, b) {
        if (a.dist !== b.dist) return a.dist - b.dist;
        return a.lib.handle < b.lib.handle ? -1 : 1;
      });
      var chosenDaw = answers.daw || null;
      if (chosenDaw) {
        for (var i = 0; i < scored.length; i++) {
          if ((scored[i].lib.daw_session_keys || []).indexOf(chosenDaw) >= 0) return scored[i];
        }
      }
      return scored[0];
    }

    var picks = [];
    var usedHandles = new Set();
    // Neighbour genres first, in configured order
    neighbours.forEach(function (ng) {
      if (picks.length >= 3) return;
      if (ng === sourceGenre) return;
      var best = bestFromGenre(ng);
      if (best && !usedHandles.has(best.lib.handle)) {
        picks.push(best);
        usedHandles.add(best.lib.handle);
        excludeHandles.add(best.lib.handle);
      }
    });

    // Fill from other genres if fewer than 3
    if (picks.length < 3) {
      var others = CANONICAL_GENRES.filter(function (g) {
        return g !== sourceGenre && neighbours.indexOf(g) < 0;
      });
      // Rank other-genre candidates by distance
      var pool = [];
      others.forEach(function (g) {
        var cands = libsInGenreExcluding(answers.format, g, catalog, config, excludeHandles, owned);
        var sourceMembers = libsInGenreForFormat(answers.format, sourceGenre, config);
        cands = cands.filter(function (p) { return !sourceMembers.has(p.handle); });
        cands.forEach(function (p) {
          var d = distance(anchor.values, p, weights);
          pool.push({ lib: p, dist: d.dist, missing_mood: d.missing, genre: g });
        });
      });
      pool.sort(function (a, b) {
        if (a.dist !== b.dist) return a.dist - b.dist;
        return a.lib.handle < b.lib.handle ? -1 : 1;
      });
      for (var i = 0; i < pool.length && picks.length < 3; i++) {
        if (!usedHandles.has(pool[i].lib.handle)) {
          picks.push(pool[i]);
          usedHandles.add(pool[i].lib.handle);
        }
      }
    }

    // Sales tie-break within SALES_TIE_BREAK_WINDOW of top dist (Phil A3 #1).
    var reordered = applySalesTieBreak(picks, config);
    // Enforce not_first: never option 1 (Phil A3 #2).
    var final = enforceNotFirst(reordered, config);

    return {
      options: final.map(function (r) {
        return {
          handle: r.lib.handle,
          title: r.lib.title,
          genre: r.genre,
          dist: r.dist,
          missing_mood: !!r.missing_mood,
          sales_12m: _sales(config, r.lib.handle),
        };
      }),
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Guitar layer (§6.4 items 6/6a — Phil A2 #6, A3 #3: now returns options[])
  // ══════════════════════════════════════════════════════════════════════════

  function pickGuitarLayer(answers, catalog, config, anchor, weights, byHandle) {
    if (answers.format === 'drums') return null;
    var guitarParts = new Set(config.guitar_parts_handles || []);
    if (!guitarParts.size) return null;

    // Path 1 — priority list (Phil A2 #6, A3 #3): walk config.guitar_layer_priority[genre]
    // in order, return the first 3 eligible libraries. Sales does NOT reorder
    // this list (Phil: "keeps Phil's order; sales doesn't override it").
    var priority = ((config.guitar_layer_priority || {})[answers.genre]) || [];
    if (priority.length) {
      var priOpts = [];
      for (var pi = 0; pi < priority.length && priOpts.length < 3; pi++) {
        var ph = priority[pi];
        var pLib = byHandle ? byHandle[ph] : null;
        if (!pLib) continue;
        if (!guitarParts.has(ph)) continue;
        var pd = distance(anchor.values, pLib, weights);
        priOpts.push({
          lib: pLib, dist: pd.dist, missing_mood: pd.missing,
          genre: pLib.genre || answers.genre,
          from_neighbours: pLib.genre !== answers.genre,
          source: 'priority',
          priority_rank: pi,
        });
      }
      // Priority preserves order; but not_first still can't be option 1.
      var priFinal = enforceNotFirst(priOpts, config);
      return {
        options: priFinal.map(function (r) {
          return {
            handle: r.lib.handle, title: r.lib.title, genre: r.genre,
            dist: round3(r.dist), missing_mood: !!r.missing_mood,
            from_neighbours: r.from_neighbours,
            source: r.source, priority_rank: r.priority_rank,
            sales_12m: _sales(config, r.lib.handle),
          };
        }),
      };
    }

    // Path 2 — default: same-genre first, then neighbours (in configured order).
    // Collect up to 3 options total. Sales tie-break applies within 5 dist of top.
    function candidatesFor(g) {
      var members = libsInGenreForFormat(answers.format, g, config);
      var out = [];
      for (var i = 0; i < catalog.length; i++) {
        var p = catalog[i];
        if (p.type !== 'Single Product') continue;
        if (!guitarParts.has(p.handle)) continue;
        if (!members.has(p.handle) && p.genre !== g) continue;
        out.push(p);
      }
      if (!out.length) return [];
      return out.map(function (p) {
        var d = distance(anchor.values, p, weights);
        return { lib: p, dist: d.dist, missing_mood: d.missing, genre: g, source: 'nearest' };
      }).sort(function (a, b) {
        if (a.dist !== b.dist) return a.dist - b.dist;
        return a.lib.handle < b.lib.handle ? -1 : 1;
      });
    }

    var picks = [];
    var seen = new Set();
    var same = candidatesFor(answers.genre);
    same.forEach(function (c) {
      if (picks.length >= 3) return;
      if (!seen.has(c.lib.handle)) {
        picks.push(Object.assign({}, c, { from_neighbours: false }));
        seen.add(c.lib.handle);
      }
    });
    var neighbours = ((config.neighbours || {})[answers.genre]) || [];
    for (var ni = 0; ni < neighbours.length && picks.length < 3; ni++) {
      var g = neighbours[ni];
      var cs = candidatesFor(g);
      for (var ci = 0; ci < cs.length && picks.length < 3; ci++) {
        if (!seen.has(cs[ci].lib.handle)) {
          picks.push(Object.assign({}, cs[ci], { from_neighbours: true }));
          seen.add(cs[ci].lib.handle);
        }
      }
    }
    if (!picks.length) return null;

    // Sales tie-break (default path only).
    var reordered = applySalesTieBreak(picks, config);
    var final = enforceNotFirst(reordered, config);

    return {
      options: final.map(function (r) {
        return {
          handle: r.lib.handle, title: r.lib.title, genre: r.genre,
          dist: round3(r.dist), missing_mood: !!r.missing_mood,
          from_neighbours: r.from_neighbours,
          source: r.source,
          sales_12m: _sales(config, r.lib.handle),
        };
      }),
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Bundle strip (§5.8) — computed per-combo
  // ══════════════════════════════════════════════════════════════════════════

  function pickStrip(matchLib, nextLib, wildcardLib) {
    var libs = [matchLib, nextLib, wildcardLib].filter(Boolean);
    if (libs.length < 2) return { type: 'none' };

    var counts = {};
    libs.forEach(function (lib) {
      (lib.curated_bundle_memberships || []).forEach(function (bh) {
        counts[bh] = counts[bh] || { count: 0, member_handles: [] };
        counts[bh].count += 1;
        counts[bh].member_handles.push(lib.handle);
      });
    });

    var qualifying = Object.keys(counts).filter(function (bh) { return counts[bh].count >= 2; });
    if (qualifying.length) {
      var matchFB = matchLib && matchLib.featured_bundle_handle;
      qualifying.sort(function (a, b) {
        if (a === matchFB && b !== matchFB) return -1;
        if (b === matchFB && a !== matchFB) return 1;
        var ca = counts[a].count, cb = counts[b].count;
        if (ca !== cb) return cb - ca;
        return 0;
      });
      var chosen = qualifying[0];
      return {
        type: 'shared',
        bundle_handle: chosen,
        member_handles: counts[chosen].member_handles,
        member_count: counts[chosen].count,
      };
    }

    var byobPct = 15;
    return {
      type: 'byob',
      pack_count: libs.length,
      save_pct: byobPct,
      handles: libs.map(function (l) { return l.handle; }),
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Top-level scorer
  // ══════════════════════════════════════════════════════════════════════════

  function fysScore(input) {
    input = input || {};
    var answers = input.answers || {};
    var catalog = input.catalog || [];
    var config = input.config || {};
    var owned = input.owned || [];
    var rounds = input.rounds || [];

    var weights = (config.weights) || { energy: 0.35, era: 0.25, production: 0.25, tone: 0.15 };

    var byHandle = {};
    for (var i = 0; i < catalog.length; i++) byHandle[catalog[i].handle] = catalog[i];

    var pool = poolFor(answers.format, answers.genre, catalog, config, owned);
    var anchor = computeAnchor(answers, byHandle, pool);

    if (rounds && rounds.length > 0) {
      // FYS-2.x hook (not implemented): rounds nudge anchor. Empty is identity.
    }

    var match = pickMatch(answers, pool, anchor, byHandle, weights, config);
    if (!match) {
      return {
        anchor: anchor,
        match: null, next: { options: [] }, wildcard: { options: [] },
        guitar_layer: { options: [] }, strip: { type: 'none' },
        reasons: { error: 'empty_pool', format: answers.format, genre: answers.genre, pool_size: 0 },
      };
    }

    var nxt = pickNextStepOptions(answers, pool, anchor, match.lib, byHandle, weights, catalog, config);
    var wc = pickWildcardOptions(answers, catalog, config, owned, anchor, match.lib, nxt.options, byHandle, weights);
    var gl = pickGuitarLayer(answers, catalog, config, anchor, weights, byHandle);
    // Guitar-layer now returns { options: [...] } or null for drums.
    if (!gl) gl = { options: [] };

    // Default strip: match + next.options[0] + wildcard.options[0]
    var defaultNext = nxt.options[0] ? byHandle[nxt.options[0].handle] : null;
    var defaultWildcard = wc.options[0] ? byHandle[wc.options[0].handle] : null;
    var strip = pickStrip(match.lib, defaultNext, defaultWildcard);

    return {
      anchor: anchor,
      match: {
        handle: match.lib.handle,
        title: match.lib.title,
        genre: match.lib.genre,
        dist: round3(match.dist),
        missing_mood: !!match.missing_mood,
        source: match.source,
        role: 'match',
      },
      next: {
        options: nxt.options.map(function (o) {
          return {
            handle: o.handle, title: o.title, genre: o.genre,
            dist: round3(o.dist), missing_mood: o.missing_mood,
            preference: o.preference, from_neighbours: o.from_neighbours,
            role: 'next',
          };
        }),
        from_neighbours: nxt.from_neighbours,
      },
      wildcard: {
        options: wc.options.map(function (o) {
          return {
            handle: o.handle, title: o.title, genre: o.genre,
            dist: round3(o.dist), missing_mood: o.missing_mood,
            role: 'wildcard',
          };
        }),
      },
      guitar_layer: gl,
      strip: strip,
      reasons: {
        format: answers.format,
        genre: answers.genre,
        pool_size: pool.length,
      },
    };
  }

  function round3(n) {
    if (!isFinite(n)) return null;
    return Math.round(n * 1000) / 1000;
  }

  function bandWord(bands, key, value) {
    var ranges = (bands || {})[key] || [];
    for (var i = 0; i < ranges.length; i++) {
      var lo = ranges[i][0], hi = ranges[i][1], word = ranges[i][2];
      if (value >= lo && value <= hi) return word;
    }
    return '';
  }

  function bandWords(lib, bands) {
    var order = ['energy', 'tone', 'era', 'production'];
    var words = [];
    for (var i = 0; i < order.length; i++) {
      var w = bandWord(bands, order[i], Number(lib['mood_' + order[i]]));
      if (w) words.push(w);
      if (words.length === 3) break;
    }
    if (!words.length) return 'balanced on every side';
    if (words.length === 1) return words[0];
    if (words.length === 2) return words[0] + ' and ' + words[1];
    return words[0] + ', ' + words[1] + ' and ' + words[2];
  }

  return {
    fysScore: fysScore,
    _computeAnchor: computeAnchor,
    _poolFor: poolFor,
    _distance: distance,
    _pickMatch: pickMatch,
    _pickNextStepOptions: pickNextStepOptions,
    _pickWildcardOptions: pickWildcardOptions,
    _pickGuitarLayer: pickGuitarLayer,
    _pickStrip: pickStrip,
    _bandWords: bandWords,
    _CANONICAL_GENRES: CANONICAL_GENRES,
  };
}));
